import React from 'react';
import { DragOverlay } from '@dnd-kit/core';
import WidgetRenderer from '../components/widgets/WidgetRenderer';
import Title from '../icons/Title';
import Paragraph from '../icons/Paragraph';
import Bars3BottomLeft from '../icons/Bars3BottomLeft';
import SeparatorIcon from '../icons/SeparatorIcon';
import ListBullet from '../icons/ListBullet';
import ImageIcon from '../icons/Image';
import Book from '../icons/Book';
import TableIcon from '../icons/Window';
import Gif from '../icons/Gif';
import PlayCircle from '../icons/PlayCircle';
import { useDossier } from './DossierContext';

interface DossierDragOverlayProps {
  activeId: ReturnType<typeof useDossier>['dossier']['_id'] | null | undefined | string | number;
  activeType: 'block' | 'chapter';
  activeItem: any;
  activeOverId: string | number | null | undefined;
}

const BLOCK_ICONS: Record<string, React.ReactNode> = {
  title: <Title width="20" height="20" />,
  paragraph: <Paragraph width="20" height="20" />,
  compositeText: <Bars3BottomLeft width="20" height="20" />,
  separator: <SeparatorIcon width="20" height="20" />,
  list: <ListBullet width="20" height="20" />,
  image: <ImageIcon width="20" height="20" />,
  summary: <Book width="20" height="20" />,
  table: <TableIcon width="20" height="20" />,
  gif: <Gif width="20" height="20" />,
  video: <PlayCircle width="20" height="20" />,
};

const BLOCK_LABELS: Record<string, string> = {
  title: 'Titre',
  paragraph: 'Paragraphe',
  compositeText: 'Texte',
  separator: 'Séparateur',
  list: 'Liste',
  image: 'Image',
  summary: 'Sommaire',
  table: 'Tableau',
  gif: 'GIF',
  video: 'Vidéo',
};

/**
 * Overlay affiché pendant un drag-and-drop dnd-kit. Trois variantes :
 *
 *   - drag de chapitre              → pill simple (titre)
 *   - drag de bloc sur la sidebar   → icône + label compact
 *   - drag de bloc dans le contenu  → preview "réaliste" du widget
 */
const DossierDragOverlay: React.FC<DossierDragOverlayProps> = ({
  activeId,
  activeType,
  activeItem,
  activeOverId,
}) => {
  const { dossier, dossierId, userRole, currentChapter, currentChapterIndex } = useDossier();
  const isHoverChapter = dossier?.content.some(c => c._id === activeOverId);

  return (
    <DragOverlay>
      {activeId ? (
        activeType === 'chapter' && activeItem ? (
          <div className="pl-4 pr-3 py-2 bg-mcdm-primary-200 dark:bg-mcdm-third-300 shadow-lg rounded opacity-90 border border-mcdm-secondary-400 w-64 truncate text-mcdm-third-500 dark:text-mcdm-primary-100 font-medium z-50">
            {activeItem.title}
          </div>
        ) : activeType === 'block' && activeItem ? (
          isHoverChapter ? (
            <div className="flex items-center px-4 py-3 bg-white dark:bg-mcdm-third-400 border border-mcdm-secondary-400 dark:border-mcdm-third-500 rounded-lg shadow-xl z-50 w-fit whitespace-nowrap opacity-95">
              <span className="text-mcdm-third-500 dark:text-mcdm-primary-100 mr-3">
                {BLOCK_ICONS[activeItem.type] ?? null}
              </span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {BLOCK_LABELS[activeItem.type] ?? 'Bloc'}
              </span>
            </div>
          ) : (
            <div className="bg-white dark:bg-mcdm-third-500 opacity-90 pointer-events-none">
              <WidgetRenderer
                block={activeItem}
                onChange={() => {}}
                onDuplicate={() => {}}
                onDelete={() => {}}
                onInsertAfter={() => {}}
                suggestions={[]}
                onAcceptSuggestion={() => {}}
                onRejectSuggestion={() => {}}
                dossierId={dossierId}
                onSuggestionCreated={() => {}}
                userRole={userRole}
                chapterIndex={currentChapterIndex}
                chapterTitle={currentChapter?.title || ''}
                dossierContent={activeItem.type === 'summary' ? dossier : undefined}
              />
            </div>
          )
        ) : null
      ) : null}
    </DragOverlay>
  );
};

export default DossierDragOverlay;
